import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button, Modal } from "react-bootstrap";
import { Link, useNavigate, useParams } from 'react-router-dom';
import { showToast } from '../config/Alert';
import arrowLeft from "../assets/images/arrow-left.png";
import skillLevel from '../assets/images/skilllivel.webp';
import { getCourseDetails } from '../services/ProgramApi';
import LoadingSpinner from './LoadingSpinner';
import config from '../config/config';

const Course = () => {
  const navigate = useNavigate();
  const { id: programId } = useParams();
  const [show, setShow] = useState(false);
  const [programDetails, setProgramDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFocusId, setSelectedFocusId] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);

  const handleShow = (skill) => {
    setSelectedSkill(skill);
    setShow(true);
  };
  const handleClose = () => setShow(false);

  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        const response = await getCourseDetails(programId);
        setProgramDetails(response.data);
        // Auto-load skill and progression if no sub-programs
        if (!response.data.sub_programs || response.data.sub_programs.length === 0) {
          setSelectedFocusId(null);
        }
      } catch (error) {
        showToast(error.response?.data?.message || "Failed to fetch program details", 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchProgramDetails();
  }, [programId]);

  const goToTeachers = () => {
    navigate(`/teachers/${programId}`, { state: { programId } });
  };

  const getTotalAvailableSlots = (skill) => {
    if (!skill || !skill.time_slots) return 0;

    return skill.time_slots.reduce((total, slot) => {
      const slotTotal = slot.time_ranges.reduce((sum, range) => {
        return sum + parseInt(range.available_slots || 0);
      }, 0);
      return total + slotTotal;
    }, 0);
  };

  const getAllTimings = (skill) => {
    if (!skill || !skill.time_slots) return "";

    const times = [];
    skill.time_slots.forEach(slot => {
      slot.time_ranges.forEach(range => {
        times.push(`${range.start_time} - ${range.end_time}`);
      });
    });

    return times.join(" | ");
  };

  const handleFocusClick = (focusId) => {
    setSelectedFocusId(focusId);
  };

  const hasSubPrograms = programDetails?.sub_programs && programDetails?.sub_programs?.length > 0;

  const filteredSkills = hasSubPrograms && selectedFocusId
    ? programDetails?.skill_levels?.filter(skill => skill.focus_id === selectedFocusId)
    : !hasSubPrograms ? programDetails?.skill_levels : [];

  const filteredProgressions = hasSubPrograms && selectedFocusId
    ? programDetails?.skill_progressions?.filter(progress => progress.focus_id === selectedFocusId)
    : !hasSubPrograms ? programDetails?.skill_progressions : [];

  if (loading) return <LoadingSpinner />;
  if (!programDetails) return <p>No course details found.</p>;

  return (
    <div className='app-container mobile-bottom'>
      <Container className="my-4">
        <div className='breadcrum bg_light_yellow'>
          <h2 className='d-flex align-items-center mb-0 fs-20'>
            <Link to='/programs'><img src={arrowLeft} width={18} className='me-2' alt="link" /></Link>
            {programDetails.program_name}</h2>
        </div>
        <div className='Course_banner_main position-relative mt-3'>
          <div className='banner_text fw-bold'>
            <h4 className='mb-0 fs-16 fw-6'>{programDetails.program_name}</h4>
            <span className='fs-8 text-muted'><i>Age Group: {programDetails.age_group} Years</i></span>
            <p className='mb-1 mt-1 fs-9'>Course Fee: <span className='text-danger'>₹ {programDetails?.final_amount}</span> per month</p>
          </div>
          <img src={`${config.Image_Base_URL}${programDetails.program_banner}`} alt="login" className='img-fluid' />
        </div>

        {hasSubPrograms &&
          programDetails.sub_programs?.map((sub, index) => (
            <Row key={index} className='mt-4'>
              <Col xs='12'>
                <h5 className='fw-6 fs-16 mb-1'>{sub.sub_title}</h5>
                <p className='fs-10'>{sub.keywords}</p>
              </Col>
              {sub.images.map((image, idx) => (
                <Col key={idx} className='text-center'>
                  <Button
                    className={`training_course ${selectedFocusId === image.id ? 'border-0 w-100' : 'border-0 w-100'}`}
                    style={{
                      backgroundColor: image.color_code,
                      cursor: 'pointer',
                      opacity: selectedFocusId && selectedFocusId !== image.id ? 0.4 : 1
                    }}
                    onClick={() => handleFocusClick(image.id)}>
                    <img src={`${config.Image_Base_URL}${image.path}`} alt={image.title} />
                    <p className='fs-8 mt-1 mb-0'>{image.title}</p>
                  </Button>
                </Col>
              ))}
            </Row>
          ))}

        {(filteredSkills?.length > 0 || !hasSubPrograms) && (
          <>
            <div className={`skillHeading d-flex align-items-center mb-2 ${!hasSubPrograms ? 'mt-4' : ''}`}>
              <h5 className='mb-0 fw-6 fs-18'>Skill Level</h5>
              <img src={skillLevel} alt="skill" className='ms-2' width={18} />
            </div>
            <Row className="mb-3 text-center">
              {(filteredSkills || programDetails.skill_levels).map((skills, index) => (
                <Col xs='4' key={skills.id || index}>
                  <Card className='blue1 p-2 mb-3'>
                    <Card.Body className='fw-6 fs-10 p-0' onClick={() => handleShow(skills)}>{skills.skill_name} <br />
                      <small className='fs-8'> ({skills.skill_period} months)</small></Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}

        {(filteredProgressions?.length > 0 || (!hasSubPrograms && programDetails.skill_progressions?.length)) && (
          <>
            <h5 className='fw-7 fs-18'>Skill Progression</h5>
            {(filteredProgressions || programDetails.skill_progressions).map((progress, index) => (
              <Card key={progress.id || index} className="mb-3 border rounded-3">
                <Card.Body className='d-flex align-items-center '>
                  <img src={`${config.Image_Base_URL}${progress.image}`} alt="imageee" width={30} />
                  <div className='ps-3 lh-sm'>
                    <h6 className='mb-1 fw-6 fs-12'>{progress.title}</h6>
                    <small className='small fw-6 fs-10'>{progress.description}</small>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </>
        )}

        <div className='mb-4 mt-2 fw-5'>
          <p className='small mb-0 fs-10'>Want to know who's guiding you child's journey?</p>
          <p className='small mb-1 fs-10'>Meet our expert teachers and learn about their experience.</p>
          <Button variant='warning' onClick={goToTeachers} size='sm' type="submit" text='dark' className='fs-12 fw-7'>Know Your Teachers</Button>
        </div>

        <div className='mb-4'>
          <p className='fw-6 fs-12 mb-0'>Tracking Your Child's Progress</p>
          <p className='fs-10 mb-1'>A detailed progress report will be provided every month.</p>
          <div className='text-center mt-3'>
            <Link to={`/pickList/${programId}`} className='bg-danger small fw-bold rounded-5 px-4 lh-sm py-2 d-inline-block text-decoration-none'>
              <span className='fs-5 text-white'>Book Now</span>
              <span className='d-block text-warning fs-8'>secure your spot and start learning! </span>
            </Link>
          </div>
        </div>

        <Modal show={show} onHide={handleClose} centered>
          <Modal.Body className='p-0'>
            <div className='p-3 pb-2'>
              <Modal.Header closeButton className='align-items-start p-0 border-0'>
                <div>
                  <h6 className="fs-12 mb-1">{selectedSkill?.skill_name}</h6>
                  <p className='fs-10 mb-0'><i>Start Your Journey</i></p>

                  {/* Dynamically generated timing & slot info */}
                  <p className='fs-10 fw-5 mb-0'>
                    <span className='text-danger'>
                      Available Slots: {getTotalAvailableSlots(selectedSkill)}
                    </span>
                    {' '}
                    (Timings: {getAllTimings(selectedSkill)})
                  </p>
                </div>
              </Modal.Header>
            </div>
            <div className='border-2 mb-2 border-bottom'></div>
            <div className='p-3 pt-0'>
              {/* <p className='fs-11 lh-md'>{selectedSkill?.skill_description}</p> */}
              <p className='fs-11 lh-md' dangerouslySetInnerHTML={{ __html: selectedSkill?.skill_description }} />
              <div className="text-end">
                <Button variant="danger" size='sm' onClick={handleClose}>
                  Close
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default Course;
